import {IPFS} from "ipfs-core"
import {ChangeEvent, useCallback, useContext, useRef, useState} from "react"
import {useSearchParams} from "react-router-dom"
import {ProfileContext} from "../../../context/Profile.context"
import {UIHelperContext} from "../../../context/UIHelper.context"
import {notifyPin, notifyUpdate} from "../../../functions/ipfs-orbit"
import {resizeImage} from "../../../functions/resize-image"
import {ProfileDB} from "../../../models/ProfileDB.model"
import styles from "./index.module.scss"

interface EditProfileProps {
  ipfs: IPFS
  profile: ProfileDB
  avatar: string
}

export const EditProfile = (props: EditProfileProps) => {
  const {ipfs, profile} = props
  const {setSnackMessage, setShowProgressBar} = useContext(UIHelperContext)
  const {setProfileReplCnt} = useContext(ProfileContext)
  const [_, setSearchParams] = useSearchParams()
  const displayName = profile.get("name")
  const bio = profile.get("bio")
  const linkedIn = profile.get("linkedInURL")
  const twitter = profile.get("twitterURL")
  const [uploadedAvatar, setUploadedAvatar] = useState<Buffer>()

  let avatarBase64: string
  if (uploadedAvatar === undefined) {
    avatarBase64 = props.avatar
  } else {
    avatarBase64 = `data:image/png;base64,${uploadedAvatar.toString("base64")}`
  }

  const displayNameInput = useRef<HTMLInputElement | null>(null)
  const linkedInInput = useRef<HTMLInputElement | null>(null)
  const twitterInput = useRef<HTMLInputElement | null>(null)
  const bioInput = useRef<HTMLTextAreaElement | null>(null)

  const onUploadAvatar = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return
    }

    const file = e.target.files[0]
    const resizedImg = await resizeImage({maxSize: 64, file})
    const arrayBuffer = await resizedImg.arrayBuffer()
    const buffer = Buffer.from(arrayBuffer)
    setUploadedAvatar(buffer)
  }, [ipfs, profile])

  const onSave = useCallback(async () => {
    setShowProgressBar(true)

    const newName = displayNameInput.current?.value
    const newBio = bioInput.current?.value
    const newLinkedIn = linkedInInput.current?.value
    const newTwitter = twitterInput.current?.value

    if (
      newName === displayName &&
      newBio === bio &&
      newLinkedIn === linkedIn &&
      newTwitter === twitter &&
      uploadedAvatar === undefined
    ) {
      setShowProgressBar(false)
      return
    }

    if (newName && newName !== displayName) {
      await profile.set("name", newName)
    }

    if (newBio && newBio !== bio) {
      await profile.set("bio", newBio)
    }

    if (newLinkedIn && newLinkedIn !== linkedIn) {
      await profile.set("linkedInURL", newLinkedIn)
    }

    if (newTwitter && newTwitter !== twitter) {
      await profile.set("twitterURL", newTwitter)
    }

    if (uploadedAvatar !== undefined) {
      const cid = await ipfs.add(uploadedAvatar)
      await notifyPin(ipfs, cid.path)
      await profile.set("avatarCID", cid.path)
      setProfileReplCnt(prevState => prevState + 1)
    }

    await notifyUpdate(ipfs, profile.address.toString())

    setShowProgressBar(false)
    setSnackMessage("Profile updated")
  }, [ipfs, profile, uploadedAvatar, setShowProgressBar, setSnackMessage])

  const onBack = useCallback(async () => {
    setSearchParams({})
  }, [setSearchParams])

  return (
    <div className={styles.EditProfileContainer}>
      <div className={styles.EditHeader}>
        Edit Profile
      </div>

      <div className={styles.EditFirstRow}>
        <div className={styles.EditLeftCol}>
          <div className={styles.EditFormTitle}>
            Display name
          </div>

          <input className={styles.NormalInput}
                 ref={displayNameInput}
                 placeholder={displayName}
                 disabled={true}
          />

          <div className={styles.EditFormTitle}>
            LinkedIn
          </div>
          <input className={styles.NormalInput}
                 ref={linkedInInput}
                 placeholder={linkedIn}
          />

          <div className={styles.EditFormTitle}>
            Twitter
          </div>
          <input className={styles.NormalInput}
                 ref={twitterInput}
                 placeholder={twitter}
          />
        </div>

        <div className={styles.EditRightCol}>
          <label>
            <img src={avatarBase64}
                 alt="Profile picture"
                 className={styles.EditAvatar}
            />
            <input id="file-input"
                   type={"file"}
                   className={styles.AvatarInput}
                   onInput={onUploadAvatar}
                   accept={"image/*"}
            >
            </input>
          </label>
          <div>
            Edit Avatar
          </div>
        </div>
      </div>

      <div className={styles.EditFormTitle}>
        Bio
      </div>
      <textarea className={styles.BioInput}
                ref={bioInput}
                placeholder={bio}
      />

      <div className={styles.BottomButtons}>
        <button className={styles.BackButton}
                onClick={onBack}
        >
          Back to profile
        </button>
        <button className={styles.SaveButton}
                onClick={onSave}
        >
          Save
        </button>
      </div>

    </div>
  )
}
